/* ============================================================
 * src/screens/channels/_bundle.tsx
 *
 * Mechanical ESM transcription of the v.3 prototype's screens-channels.jsx.
 * The prototype destructured React from a global and exposed each screen
 * via Object.assign(window, ...). This file restores the same surface as
 * named ESM exports; per-component files in this directory re-export the
 * screen the route table maps to so downstream wiring tasks can edit a
 * single component without touching the bundle.
 * ============================================================ */
/* eslint-disable react-refresh/only-export-components, @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Icon, Btn, Pill, CircaTag, Panel, PageHead, Breadcrumbs, Tabs, Spark,
  StatCard, ScoreRing, HelpDot, TimerChip, AccessGate, AdminGate, CapPill,
  FreshnessBanner, AuditTrailLink, Field, Input, Textarea, Select, Toggle,
  SegmentedControl, Thumb, Avatar, EmptyState, Skeleton, useLoading,
  SkeletonStatRow, SkeletonCardGrid, SkeletonTable, SkeletonPanel, SkeletonList,
  Modal, SlideOver, ConfirmModal, Dropdown, Progress, TONES,
  useToast, useMutationWithToast,
} from '../../components/primitives';
// T318 — the mock fixture import retired. ChannelDetail and its tabs bind
// to `useChannelList()` for the channel context; decorative per-card
// data (primaryColor, nowPlaying, viewers, uptime, providerSpend, members)
// that the real schema does not expose is honestly rendered as em-dash
// placeholders rather than fabricated from the fixture.
import { fmtDur, statusLabels } from '../../lib/format';
import { navigate } from '../../lib/nav';
import { PodcastSettingsPanel } from '../content/_audio';
import { AssetPickerModal, DestinationsWizard } from '../pending/_bundle';
import { MonitorDetail } from '../home/_bundle';
import { useChannelList, type ChannelListRow } from '../../hooks/useChannelList';
import { adaptChannelStatus } from '../../lib/adapters';
import { api } from '@convex/api';
import { useMutationWithToast as useHonestMutation } from '../../hooks/useMutationWithToast';
// T333 (FR-011, WIRE-5) — `/destinations` reads the public YouTube quota
// query landed by `convex/queries/youtubeApiProjects.ts` and the existing
// public YouTube live-status query (`api.channelYoutube.listAll`). Both
// are auth-gated server-side; the cards render the loading skeleton until
// either subscription resolves and an honest em-dash placeholder for the
// unwired "Publicaciones fallidas (24h)" tile (NFR-006 — no speculative
// query family).
import { useQuery } from 'convex/react';
// (api already imported above)

// Silence unused-import lint for primitives this file may not reference.
void React; void useState; void useEffect; void useRef; void useCallback; void useMemo;
void Icon; void Btn; void Pill; void CircaTag; void Panel; void PageHead; void Breadcrumbs;
void Tabs; void Spark; void StatCard; void ScoreRing; void HelpDot; void TimerChip;
void AccessGate; void AdminGate; void CapPill; void FreshnessBanner; void AuditTrailLink;
void Field; void Input; void Textarea; void Select; void Toggle; void SegmentedControl;
void Thumb; void Avatar; void EmptyState; void Skeleton; void useLoading;
void SkeletonStatRow; void SkeletonCardGrid; void SkeletonTable; void SkeletonPanel; void SkeletonList;
void Modal; void SlideOver; void ConfirmModal; void Dropdown; void Progress; void TONES;
void useToast; void useMutationWithToast;
void navigate;

/* ============================================================
   CIRCA — Channels arc
   ============================================================ */

// statusTone — maps a channel status string to a Pill tone token. Accepts
// BOTH the closed six-literal backend `channelStatus` union (wired path,
// CLAUDE.md §18 invariant — render via adaptChannelStatus, NEVER the raw
// literal in copy) AND the prototype's mock vocabulary ('live'/'adbreak')
// because legacy mock-backed callers in this file (ChannelDetail, GoLive,
// StreamKeys) still flow through here. New wired paths use the canonical
// six-literal set; the mock literals are intentionally tolerated until
// those screens are wired in a follow-up pass.
function statusTone(s: string): 'live' | 'good' | 'warn' | 'bad' | 'neutral' {
  switch (s) {
    case 'running':
    case 'live':     return 'live';
    case 'ad_break':
    case 'adbreak':
    case 'starting':
    case 'stopping': return 'warn';
    case 'error':    return 'bad';
    case 'idle':     return 'neutral';
    default:         return 'neutral';
  }
}

export function ChannelsList() {
  const loading = useLoading(650);
  const [view, setView] = useState('grid');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('Todos');
  // WIRE-3 (T330 / FR-009) — channels grid rides on the canonical
  // `useChannelList()` composition hook (2-query batch — NFR-004). The
  // prototype's per-card presentational fields (`viewers`, `uptime`,
  // `spark`, `provider`, `playbackMode`, `primaryColor`, `nowPlaying`)
  // are NOT on the wired row — they are omitted (or rendered as the em-dash
  // "—" placeholder) rather than fabricated. Per-channel uptime/SLO
  // composition belongs in `useChannelCard`, NEVER added to the list hook
  // (the 2-query structural test in tests/hooks/useChannelList.test.ts
  // pins that invariant).
  const channelRows = useChannelList();
  // The wired status filter dropdown uses the closed-six backend literals
  // (via adaptChannelStatus for the labels). The prototype's options
  // ('En vivo', 'Inactivo', 'Error', 'Iniciando', 'Corte publicitario')
  // map onto the canonical labels by going through the adapter. The
  // 'Todos' option short-circuits the filter.
  const filteredRows: ChannelListRow[] | undefined = useMemo(() => {
    if (channelRows === undefined) return undefined;
    const needle = q.trim().toLowerCase();
    return channelRows.filter((r) => {
      const matchesText =
        !needle ||
        r.name.toLowerCase().includes(needle) ||
        (r.slug ?? '').toLowerCase().includes(needle);
      const matchesStatus =
        status === 'Todos' || adaptChannelStatus(r.backendStatus) === status;
      return matchesText && matchesStatus;
    });
  }, [channelRows, q, status]);
  // KPI strip derivations — all from the wired rows. We surface honest
  // em-dash placeholders for the metrics the real schema does not carry
  // (total viewers, average uptime); never fabricate values.
  const liveCount = channelRows?.filter(
    (r) => r.backendStatus === 'running' || r.backendStatus === 'ad_break',
  ).length;
  const errorCount = channelRows?.filter((r) => r.backendStatus === 'error').length;
  const totalChannels = channelRows?.length;
  const tile = (n: number | undefined): number | string => (n === undefined ? '—' : n);
  return (
    <div>
      <PageHead title="Canales" description="Cada transmisión 24×7 que operas desde Circa."
        actions={<AccessGate perm="channel_create"><Btn variant="solid" icon="plus" onClick={() => navigate('/channels/new')}>Nuevo canal</Btn></AccessGate>} />
      {loading || filteredRows === undefined ? <><SkeletonStatRow /><div style={{ height: 64 }} /><SkeletonCardGrid n={6} media={150} /></> : filteredRows.length === 0 && channelRows && channelRows.length === 0 ? (
        <EmptyState icon="radio" title="Sin canales" body="Aún no se ha creado ningún canal en este espacio." />
      ) : <>
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Al aire ahora" value={tile(liveCount)} hint={totalChannels === undefined ? '' : `de ${totalChannels} canales`} tone="good" icon="radio" />
        <StatCard label="Espectadores totales" value="—" hint="concurrentes" icon="eye" />
        <StatCard label="Con error" value={tile(errorCount)} tone={(errorCount ?? 0) > 0 ? 'bad' : undefined} icon="alertTriangle" onClick={() => navigate('/monitoring')} />
        <StatCard label="Disponibilidad media" value="—" hint="últimos 30 días" icon="gauge" />
      </div>
      <Panel pad={16} style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ width: 300 }}><Input value={q} onChange={setQ} icon="search" placeholder="Buscar por nombre o slug" /></div>
          <div style={{ width: 170 }}><Select value={status} onChange={setStatus} full options={['Todos', 'En vivo', 'Inactivo', 'Error', 'Iniciando', 'Pausa publicitaria']} /></div>
          <div style={{ width: 170 }}><Select value="Todos" onChange={() => {}} full options={['Todos', 'AWS', 'Hetzner', 'Gcore', 'Cloudflare']} /></div>
          <div style={{ flex: 1 }} />
          <SegmentedControl value={view} onChange={setView} options={[{ value: 'grid', icon: 'grid' }, { value: 'list', icon: 'list' }]} />
        </div>
      </Panel>
      <div style={{ marginBottom: 14 }}><Pill tone="neutral">{filteredRows.length} canales</Pill></div>
      {view === 'grid' ? (
        <div className="circa-grid-3">
          {filteredRows.map(c => {
            const onAir = c.backendStatus === 'running' || c.backendStatus === 'ad_break';
            const statusLabel = adaptChannelStatus(c.backendStatus);
            return (
            <div key={c._id} onClick={() => navigate('/channels/' + c._id)} className="circa-lift" style={{ border: '1px solid var(--border-cool)', borderRadius: 22, overflow: 'hidden', cursor: 'pointer', background: 'var(--canvas)' }}>
              <Thumb radius={0} label={onAir ? '' : 'Tarjeta de espera'} live={c.backendStatus === 'running'}>
                <span style={{ position: 'absolute', top: 12, right: 12 }}><Pill tone={statusTone(c.backendStatus)}>{statusLabel}</Pill></span>
                {/* Honest placeholder for the viewer-count overlay — the wired
                    row carries no viewers count; never fabricate one. */}
              </Thumb>
              <div style={{ padding: 18 }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 500, marginBottom: 4 }}>{c.name}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, fontSize: 13, color: 'var(--body-muted)', minHeight: 20 }}>
                  {onAir
                    ? <span style={{ color: 'var(--muted-slate)' }}>—</span>
                    : <span style={{ color: 'var(--muted-slate)' }}>Fuera del aire</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {c.edgeProvider && <Pill tone="neutral" size="sm">{c.edgeProvider}</Pill>}
                  </div>
                  {/* Spark omitted — the real schema has no series field. */}
                </div>
              </div>
            </div>
          ); })}
        </div>
      ) : (
        <Panel pad={0}>
          {filteredRows.map((c, i) => (
            <div key={c._id} onClick={() => navigate('/channels/' + c._id)} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px', borderBottom: i < filteredRows.length - 1 ? '1px solid var(--lightest-gray)' : 'none', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--snow)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ width: 64, flexShrink: 0 }}><Thumb radius={8} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: 15 }}>{c.name}</div>
                <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>{c.slug ?? '—'}</div>
              </div>
              {c.edgeProvider && <Pill tone="neutral" size="sm">{c.edgeProvider}</Pill>}
              <Pill tone={statusTone(c.backendStatus)}>{adaptChannelStatus(c.backendStatus)}</Pill>
              <Icon name="chevronR" size={16} color="var(--muted-slate)" />
            </div>
          ))}
        </Panel>
      )}
      </>}
    </div>
  );
}

export function ChannelCreate() {
  // Real `api.channels.createChannel` (convex/channels.ts:148). The previous
  // implementation called `useMutationWithToast()` with zero args (a v.2
  // legacy shape that just toasted "Canal creado." without ever hitting the
  // backend), then navigated home — the canonical FR-013 anti-pattern.
  const createChannel = useHonestMutation(api.channels.createChannel, {
    logLabel: 'channels_create',
    mutationName: 'channels.createChannel',
    successMessage: 'Canal creado.',
  });
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [mode, setMode] = useState('loop');
  const [timezone, setTimezone] = useState('America/Mexico_City');
  const [error, setError] = useState<string | null>(null);
  const auto = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const effectiveSlug = slug || auto;

  const submit = async () => {
    setError(null);
    const trimmedName = name.trim();
    if (!trimmedName) { setError('Ingresa un nombre para el canal.'); return; }
    if (!effectiveSlug) { setError('El slug no puede estar vacío.'); return; }
    try {
      await createChannel.run({
        name:        trimmedName,
        slug:        effectiveSlug,
        timezone,
        loopEnabled: mode === 'loop',
      });
      navigate('/channels');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo crear el canal.');
    }
  };

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <PageHead crumbs={[{ label: 'Canales', href: '#/channels' }, { label: 'Nuevo' }]} title="Crear un canal" description="Un canal es una transmisión 24×7 que difundes a uno o más destinos." />
      <Panel pad={32}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Field label="Nombre del canal" hint="Los operadores ven este nombre en todas partes." required><Input value={name} onChange={setName} placeholder="Mi canal" /></Field>
          <div style={{ height: 1, background: 'var(--lightest-gray)' }} />
          <Field label="Slug" hint="Identificador en la URL pública." required><Input value={slug || auto} onChange={setSlug} prefix="circa.tv/v/" placeholder="mi-canal" /></Field>
          <div style={{ height: 1, background: 'var(--lightest-gray)' }} />
          <Field label="Zona horaria"><Select value={timezone} onChange={setTimezone} full options={['America/Mexico_City', 'America/Bogota', 'America/Buenos_Aires', 'Europe/Madrid']} /></Field>
          <Field label="Modo de reproducción"><SegmentedControl value={mode} onChange={setMode} options={[{ value: 'loop', label: 'Bucle' }, { value: 'linear', label: 'Lineal' }]} /></Field>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {/* Pre-roll / post-roll toggles are not yet wired to a real
                `defaultScte35Config` field — they accepted clicks but
                persisted nothing. Showing disabled until SCTE-35 defaults
                wiring lands. */}
            <Field label="Anuncio inicial (pre-roll)"><Toggle on={false} onChange={() => {}} disabled /></Field>
            <Field label="Anuncio final (post-roll)"><Toggle on={false} onChange={() => {}} disabled /></Field>
          </div>
          {error && <p role="alert" style={{ margin: 0, fontSize: 13, color: 'var(--bad)' }}>{error}</p>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 32 }}>
          <Btn variant="ghost" onClick={() => navigate('/channels')}>Cancelar</Btn>
          <Btn variant="solid" loading={createChannel.loading} onClick={() => { void submit(); }}>Crear canal</Btn>
        </div>
      </Panel>
    </div>
  );
}

export function ChannelDetail({ id }: { id?: string }) {
  // T318 — wired channel context. Renders an honest skeleton while the
  // list resolves and an EmptyState when the route id does not match
  // any real channel row. The legacy mock-shape (`id`, `status`, `slug`,
  // `primaryColor`, `nowPlaying`) is adapted to the wired row's
  // (`_id`, `backendStatus`, `slug`); decorative-only fields collapse
  // to honest em-dash placeholders downstream.
  const rows = useChannelList();
  const real = rows?.find(x => x._id === id || x.slug === id);
  const tab = (window.location.hash.split('?t=')[1]) || 'overview';
  const [active, setActive] = useState(tab);
  const { toast } = useToast();
  if (rows === undefined) {
    return <Skeleton h={420} />;
  }
  if (real === undefined) {
    return <EmptyState icon="alertTriangle" title="Canal no encontrado" body="No se localizó el canal solicitado." />;
  }
  // Bridge the wired row to the legacy `c` shape the tab components
  // expect. Fields the real schema does NOT expose are explicitly null
  // so child render paths render the em-dash placeholder.
  const c = {
    id: real._id,
    _id: real._id,
    name: real.name,
    slug: real.slug,
    status: real.backendStatus,
    backendStatus: real.backendStatus,
    primaryColor: undefined as string | undefined,
    nowPlaying: undefined as string | undefined,
    viewers: undefined as number | undefined,
    uptime: undefined as number | undefined,
    quality: undefined as number | undefined,
  };
  const onAir = real.backendStatus === 'running' || real.backendStatus === 'ad_break';
  const tabs = [{ id: 'overview', label: 'Resumen' }, { id: 'schedule', label: 'Programación' }, { id: 'live', label: 'En vivo' }, { id: 'health', label: 'Salud' }, { id: 'settings', label: 'Ajustes' }];
  return (
    <div>
      <PageHead crumbs={[{ label: 'Canales', href: '#/channels' }, { label: c.name }]} title={c.name}
        actions={onAir
          ? <Btn variant="danger" icon="pause" onClick={() => toast({ variant: 'warning', message: 'Transmisión detenida.' })}>Detener transmisión</Btn>
          : <AccessGate perm="channel_mode_set"><Btn variant="solid" iconRight="arrowRight" onClick={() => navigate('/channels/' + c.id + '/go-live')}>Salir al aire</Btn></AccessGate>} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: -16, marginBottom: 24 }}>
        <span className="t-mono" style={{ fontSize: 12, color: 'var(--muted-slate)' }}>{c.slug}</span>
        <Pill tone={statusTone(c.backendStatus)}>{adaptChannelStatus(c.backendStatus)}</Pill>
        <div style={{ flex: 1 }} />
        <Btn size="sm" variant="text" icon="chart" onClick={() => navigate('/channels/' + c.id + '/performance')}>Rendimiento</Btn>
      </div>
      <Tabs tabs={tabs} active={active} onChange={setActive} style={{ marginBottom: 24 }} />
      {active === 'overview' && <ChannelOverview c={c} />}
      {active === 'schedule' && <ChannelScheduleTab c={c} />}
      {active === 'live' && <ChannelLiveTab c={c} />}
      {active === 'health' && <ChannelHealthTab c={c} />}
      {active === 'settings' && <ChannelSettingsTab c={c} />}
    </div>
  );
}

function ChannelOverview({ c }) {
  const onAir = c.status === 'live' || c.status === 'adbreak';
  return (
    <div className="circa-cols-8-4">
      <Panel title="Reproduciendo ahora" pad={0}>
        <div style={{ padding: 24 }}>
          <Thumb label={onAir ? '' : 'Fuera del aire — mostrando tarjeta de espera'} live={c.backendStatus === 'running'} />
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 400, margin: '18px 0 6px' }}>{onAir ? '—' : 'Sin contenido al aire'}</div>
          <div className="t-mono" style={{ fontSize: 12, color: 'var(--muted-slate)', marginBottom: 16 }}>{onAir ? 'EN VIVO · INICIÓ HACE 24 MIN' : '—'}</div>
          {onAir && <Progress value={42} />}
          <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
            <AccessGate perm="channel_mode_set"><Btn variant="ghost" size="sm" iconRight="skip">Saltar al siguiente</Btn></AccessGate>
            <AccessGate perm="channel_mode_set"><Btn variant="ghost" size="sm" icon="zap">Forzar corte de anuncio</Btn></AccessGate>
          </div>
        </div>
      </Panel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <Panel title="A continuación">
          {/* T318 — honest placeholder. There is no public per-channel
              upcoming-program query in this milestone. */}
          <EmptyState
            icon="clock"
            title="Sin programación próxima"
            body="No hay próximos elementos para mostrar para este canal."
          />
        </Panel>
        <div className="circa-grid-2-tight">
          <StatCard label="Espectadores" value="—" icon="eye" />
          <StatCard label="Disponibilidad" value="—" icon="gauge" />
        </div>
        <Panel title="Destinos">
          <EmptyState
            icon="send"
            title="Destinos no disponibles"
            body="Abre Destinos para gestionar los puntos de publicación del canal."
            action={<Btn size="sm" variant="text" iconRight="arrowRight" onClick={() => navigate('/destinations')}>Administrar destinos</Btn>}
          />
        </Panel>
        <Panel title="Calidad (última hora)" right={<Btn size="sm" variant="text" onClick={() => navigate('/channels/' + c.id + '/performance')}>Detalle</Btn>}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ flex: 1, fontSize: 12, color: 'var(--muted-slate)' }}>Sin datos de calidad disponibles.</div>
          </div>
        </Panel>
      </div>
    </div>
  );
}

function ChannelScheduleTab({ c }: { c: { name: string } }) {
  // T318 — honest empty state. The legacy "Próximas 12 horas" timeline
  // had no real per-channel schedule query backing it. Operators are
  // routed to the wired schedules editor for real schedule work.
  return (
    <div>
      <Panel pad={16} style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14 }}>Programación activa: <b>{c.name}</b></span>
          <Btn size="sm" variant="text" iconRight="arrowRight" onClick={() => navigate('/schedules')}>Abrir en el editor</Btn>
        </div>
      </Panel>
      <Panel title="Próximas 12 horas">
        <EmptyState
          icon="clock"
          title="Sin programación próxima"
          body="No hay bloques programados para mostrar."
        />
      </Panel>
    </div>
  );
}

function ChannelLiveTab({ c }: { c: { id: string } }) {
  // T318 — fixed-text counts removed (they were prototype placeholders,
  // not wired). Each tile shows an em-dash until the real per-channel
  // event-rollup query lands.
  return (
    <div>
      <FreshnessBanner show message="Los datos de señal en vivo pueden ir ligeramente atrasados." />
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Eventos (15 min)" value="—" icon="activity" />
        <StatCard label="Validaciones (15 min)" value="—" icon="checkCircle" />
        <StatCard label="Alertas activas" value="—" icon="bell" />
        <StatCard label="Transmisiones (7 d)" value="—" icon="radio" />
      </div>
      <MonitorDetail id={c.id} />
    </div>
  );
}

function ChannelHealthTab(_props: { c: unknown }) {
  // T318 — health rollups (bloqueos / huecos / caídas / errores /
  // disponibilidad) have no public per-channel query in this milestone.
  // The panel renders honest neutral placeholders rather than the
  // prototype's randomized series.
  void _props;
  return (
    <div>
      <Panel title="Calidad (última hora)" right={<CapPill>Datos aproximados</CapPill>} style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 32, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="circa-grid-4" style={{ flex: 1, minWidth: 280 }}>
            <StatCard label="Bloqueos" value="—" hint="última hora" />
            <StatCard label="Huecos" value="—" hint="última hora" />
            <StatCard label="Caídas" value="—" hint="última hora" />
            <StatCard label="Errores" value="—" hint="última hora" />
          </div>
        </div>
      </Panel>
      <Panel title="Disponibilidad — últimos 30 días">
        <EmptyState
          icon="gauge"
          title="Sin datos de disponibilidad"
          body="No hay métricas SLO para mostrar en este canal."
        />
      </Panel>
    </div>
  );
}

function ChannelSettingsTab({ c }) {
  const m = useMutationWithToast();
  const subs = ['General', 'Programación', 'Destinos', 'Pódcast', 'Tarjeta de espera', 'Cortes de anuncio', 'Notificaciones', 'Avanzado'];
  const [sub, setSub] = useState('General');
  const [transferOpen, setTransferOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [picker, setPicker] = useState(false);
  return (
    <div className="circa-cols-4-8">
      <Panel pad={8}>
        {subs.map(s => <button key={s} onClick={() => setSub(s)} style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
          background: sub === s ? 'var(--pale-green)' : 'transparent', color: sub === s ? 'var(--deep-green)' : 'var(--body-muted)', borderRadius: 8, padding: '10px 12px', fontSize: 14, fontWeight: sub === s ? 500 : 400, fontFamily: 'var(--font-sans)' }}>{s}</button>)}
      </Panel>
      <Panel pad={28}>
        {sub === 'General' && <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Field label="Nombre"><Input value={c.name} onChange={() => {}} /></Field>
          <Field label="Slug"><Input value={c.slug} onChange={() => {}} prefix="circa.tv/v/" /></Field>
          <Field label="Descripción"><Textarea value={c.description} onChange={() => {}} /></Field>
          <div><Btn variant="solid" onClick={() => m(() => {})}>Guardar cambios</Btn></div>
        </div>}
        {sub === 'Avanzado' && <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <AccessGate perm="channel_transfer"><Btn variant="ghost" icon="users" onClick={() => setTransferOpen(true)}>Transferir canal</Btn></AccessGate>
          <AccessGate perm="channel_create"><Btn variant="ghost" icon="copy" onClick={() => m(() => {}, { success: 'Canal duplicado.' })}>Duplicar canal</Btn></AccessGate>
          <AccessGate perm="channel_archive"><Btn variant="danger" icon="trash" onClick={() => setArchiveOpen(true)}>Archivar canal</Btn></AccessGate>
        </div>}
        {sub === 'Tarjeta de espera' && <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--body-muted)' }}>La tarjeta de espera se muestra cuando el canal está fuera del aire.</p>
          <Thumb color={c.primaryColor} radius={14} label="Tarjeta de espera actual" />
          <div><Btn variant="ghost" icon="film" onClick={() => setPicker(true)}>Elegir contenido</Btn></div>
        </div>}
        {sub === 'Pódcast' && <PodcastSettingsPanel channel={c} />}
        {sub !== 'General' && sub !== 'Avanzado' && sub !== 'Tarjeta de espera' && sub !== 'Pódcast' && <EmptyState icon="settings" title={sub} body="Configura los ajustes de esta sección. Los cambios se guardan por canal." />}
      </Panel>
      <ConfirmModal open={transferOpen} onClose={() => setTransferOpen(false)} title="Transferir canal" typed={c.slug}
        body={`Escribe el slug del canal para confirmar la transferencia de "${c.name}" a otro administrador.`} confirmLabel="Transferir" onConfirm={() => m(() => {}, { success: 'Canal transferido.' })} />
      <ConfirmModal open={archiveOpen} onClose={() => setArchiveOpen(false)} title="Archivar canal" danger typed={c.slug}
        body="Archivar detiene el canal y lo oculta de la lista, pero conserva todos los datos. Puedes restaurarlo después." confirmLabel="Archivar" onConfirm={() => m(() => {}, { success: 'Canal archivado.' })} />
      <AssetPickerModal open={picker} onClose={() => setPicker(false)} onPick={() => m(() => {}, { success: 'Tarjeta de espera actualizada.' })} />
    </div>
  );
}

export function GoLive({ id }: { id?: string }) {
  // T318 — wired channel context. Renders an honest skeleton while the
  // list resolves and an EmptyState when the route id has no real match.
  const rows = useChannelList();
  const real = rows?.find(x => x._id === id || x.slug === id);
  const { toast } = useToast();
  const [verified, setVerified] = useState(false);
  if (rows === undefined) {
    return <Skeleton h={420} />;
  }
  if (real === undefined) {
    return <EmptyState icon="alertTriangle" title="Canal no encontrado" body="No se localizó el canal solicitado." />;
  }
  const c = { id: real._id, _id: real._id, name: real.name, slug: real.slug };
  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <PageHead crumbs={[{ label: 'Canales', href: '#/channels' }, { label: c.name, href: '#/channels/' + c.id }, { label: 'Salir al aire' }]} title="Salir al aire" description={`${c.name} — configura tu codificador y comienza a transmitir.`} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <Panel title="Puntos de conexión">
          {[['Principal', 'rtmps://ingest.circa.tv/live'], ['Respaldo', 'rtmps://backup.circa.tv/live']].map(([k, url]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--lightest-gray)' }}>
              <Pill tone={k === 'Principal' ? 'info' : 'neutral'} size="sm">{k}</Pill>
              <span className="t-mono" style={{ flex: 1, fontSize: 13, textTransform: 'none', overflow: 'hidden', textOverflow: 'ellipsis' }}>{url}</span>
              <Btn size="sm" variant="ghost" icon="copy" onClick={() => toast({ variant: 'success', message: 'Copiado al portapapeles.' })}>Copiar</Btn>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            <Pill tone="neutral">RTMPS</Pill><Pill tone="neutral">4500 kbps recomendado</Pill><Pill tone="neutral">1920×1080 @ 30 fps</Pill>
          </div>
        </Panel>
        <Panel title="Clave de entrada">
          <p style={{ margin: '0 0 14px', fontSize: 14, color: 'var(--body-muted)' }}>Tu credencial está oculta por seguridad. Revélala desde la página de credenciales (solo administradores).</p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <AccessGate perm="streamkey_reveal"><Btn variant="ghost" icon="key" onClick={() => navigate('/channels/' + c.id + '/input-keys')}>Administrar credenciales</Btn></AccessGate>
            <Btn variant="ghost" icon="sliders" onClick={() => navigate('/channels/' + c.id + '/encoder')}>Configurar codificador</Btn>
          </div>
        </Panel>
        {/* G15 — honest not-verified state, never false-green */}
        <Panel title="Estado de la conexión">
          {!verified ? (
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: 16, background: 'rgba(245,158,11,0.06)', borderRadius: 14, border: '1px solid rgba(245,158,11,0.3)' }}>
              <Icon name="alertTriangle" size={18} color="var(--warn)" style={{ marginTop: 1 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>Conexión no verificada</div>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--body-muted)' }}>Aún no hemos recibido señal de tu codificador. No mostraremos un estado “en vivo” hasta confirmar la señal entrante.</p>
              </div>
              <Btn size="sm" variant="ghost" icon="refresh" onClick={() => { setVerified(true); toast({ variant: 'success', message: 'Señal detectada.' }); }}>Probar señal</Btn>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 16, background: 'var(--pale-green)', borderRadius: 14 }}>
              <Icon name="checkCircle" size={18} color="var(--good)" />
              <span style={{ fontSize: 14 }}>Señal entrante confirmada. Puedes salir al aire.</span>
            </div>
          )}
          <div style={{ marginTop: 16 }}>
            <Btn variant="solid" size="lg" disabled={!verified} icon="radio" onClick={() => { toast({ variant: 'success', message: '¡Al aire!' }); navigate('/channels/' + c.id); }}>Comenzar transmisión</Btn>
          </div>
        </Panel>
      </div>
    </div>
  );
}

export function StreamKeys({ id }: { id?: string }) {
  // T318 — wired channel context. The dedicated wired StreamKeys surface
  // (sibling file `StreamKeys.tsx`) is the production route; this legacy
  // bundle entry mirrors the same wiring pattern for any old route
  // pointing at this export.
  const rows = useChannelList();
  const real = rows?.find(x => x._id === id || x.slug === id);
  const { toast } = useToast();
  const [reveal, setReveal] = useState<string | null>(null); // 'warn' | 'shown'
  const [ack, setAck] = useState(false);
  if (rows === undefined) {
    return <AdminGate><Skeleton h={420} /></AdminGate>;
  }
  if (real === undefined) {
    return <AdminGate><EmptyState icon="alertTriangle" title="Canal no encontrado" body="No se localizó el canal solicitado." /></AdminGate>;
  }
  const c = { id: real._id, _id: real._id, name: real.name, slug: real.slug };
  return (
    <AdminGate>
      <PageHead crumbs={[{ label: 'Canales', href: '#/channels' }, { label: c.name, href: '#/channels/' + c.id }, { label: 'Credenciales' }]}
        title="Credenciales de entrada" description="Administra las credenciales principal y de respaldo. Las credenciales reveladas se borran solas a los 60 segundos."
        actions={<AuditTrailLink onClick={() => toast({ variant: 'info', message: 'Abriendo historial de auditoría…' })} />} />
      <Panel pad={0}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px 140px', padding: '12px 20px', borderBottom: '1px solid var(--border-cool)', fontSize: 12, color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
          <span>Principal</span><span>Respaldo</span><span>Estado</span><span>Acciones</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px 140px', padding: '16px 20px', alignItems: 'center' }}>
          <span className="t-mono" style={{ fontSize: 13, textTransform: 'none' }}>••••••••1234</span>
          <span className="t-mono" style={{ fontSize: 13, textTransform: 'none' }}>••••••••5678</span>
          <Pill tone="good" size="sm">Activa</Pill>
          <div style={{ display: 'flex', gap: 6 }}>
            <Btn size="sm" variant="ghost" onClick={() => { setReveal('warn'); setAck(false); }}>Revelar</Btn>
            <Btn size="sm" variant="ghost" icon="refresh" onClick={() => toast({ variant: 'success', message: 'Credencial rotada.' })} />
          </div>
        </div>
        {reveal === 'warn' && (
          <div style={{ margin: '0 20px 20px', padding: 20, borderRadius: 22, border: '1px solid var(--border-cool)' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 14 }}>
              <Icon name="alertTriangle" size={20} color="var(--warn)" />
              <p style={{ margin: 0, fontSize: 14, color: 'var(--body-muted)', lineHeight: 1.5 }}>La credencial será visible en este dispositivo durante 60 segundos y luego se borrará automáticamente. Cualquiera con esta credencial puede transmitir al destino asociado.</p>
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginBottom: 16, cursor: 'pointer' }}>
              <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} /> Entiendo y quiero revelar la credencial ahora.
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn variant="ghost" onClick={() => setReveal(null)}>Cancelar</Btn>
              <Btn variant="solid" disabled={!ack} onClick={() => setReveal('shown')}>Revelar credencial</Btn>
            </div>
          </div>
        )}
        {reveal === 'shown' && (
          <div style={{ margin: '0 20px 20px', padding: 20, borderRadius: 22, background: 'linear-gradient(135deg, var(--forest-a), var(--forest-b))', color: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 500 }}>Tu credencial — mantenla privada</span>
              <TimerChip seconds={59} onDone={() => setReveal(null)} />
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <code style={{ fontFamily: 'var(--font-mono)', fontSize: 18, background: 'rgba(0,0,0,0.25)', padding: '10px 14px', borderRadius: 8, flex: 1 }}>live_a8F2k9Xq7sLm3pR1234</code>
              <Btn variant="onDark" icon="copy" onClick={() => toast({ variant: 'success', message: 'Credencial copiada.' })}>Copiar</Btn>
            </div>
          </div>
        )}
      </Panel>
    </AdminGate>
  );
}

/**
 * T333 (FR-011, WIRE-5) — `/destinations` wired to real Convex reads.
 *
 * Three honest data sources:
 *   - `api.channelYoutube.listAll` — workspace YouTube live-status rollup.
 *     Powers the "Transmisiones activas" tile (live-state row count) and
 *     the operativo/atención panel-title pill.
 *   - `api.queries.youtubeApiProjects.listPublic` — Google Cloud project
 *     quota pool. Powers "Cuota usada hoy" / "Cuota restante" and the
 *     per-project capacity rows below.
 *   - The "Publicaciones fallidas (24h)" tile renders an honest em-dash:
 *     no public `multiPlatformPublishLedger` failure-summary query exists
 *     today, and FR-012 / NFR-006 forbid wiring against a non-existent
 *     reference. A future query lands the real count without touching
 *     this surface.
 *
 * Render order (three-state pattern per CLAUDE.md §3, §1C.5):
 *   - LOADING: either `useQuery` returns `undefined` → SkeletonStatRow +
 *     SkeletonPanel placeholder. Never a fabricated number.
 *   - EMPTY: `projects.length === 0` (deployment not yet provisioned) →
 *     EmptyState in the capacity panel; stat cards show em-dashes.
 *   - DATA: render the real counts and per-project rows.
 *
 * The status pill on the panel header reflects the YouTube live signal:
 *   - "Operativo" / good (dot) when there is at least one live row and
 *     zero errors;
 *   - "Atención" / warn (dot) when at least one row is in error;
 *   - "Sin señal" / neutral (no dot) when there is no live row at all.
 */
export function Destinations() {
  const [wiz, setWiz] = useState(false);
  const youtubeRows = useQuery(api.channelYoutube.listAll, {}) as
    | Array<{ youtubeStreamStatus: 'live' | 'none' | 'upcoming' | 'unknown' | 'error' }>
    | undefined;
  const quota = useQuery(api.queries.youtubeApiProjects.listPublic, {}) as
    | {
        projects: Array<{
          _id: string;
          projectId: string;
          quotaUsedToday: number;
          quotaLimitDaily: number;
          status: 'active' | 'rate-limited' | 'disabled';
          lastResetAt: number;
        }>;
        summary: {
          projectCount: number;
          activeCount: number;
          limitedCount: number;
          disabledCount: number;
          totalQuotaUsedToday: number;
          totalQuotaLimitDaily: number;
          quotaUsedPercent: number;
          quotaRemainingPercent: number;
        };
      }
    | undefined;

  const loading = youtubeRows === undefined || quota === undefined;

  // Derive the panel-header status pill honestly. Until the live rollup
  // arrives we cannot claim "Operativo".
  const ytLiveCount = youtubeRows?.filter((r) => r.youtubeStreamStatus === 'live').length;
  const ytErrorCount = youtubeRows?.filter((r) => r.youtubeStreamStatus === 'error').length;
  const headerPill = loading
    ? <Pill tone="neutral">Cargando…</Pill>
    : (ytErrorCount ?? 0) > 0
      ? <Pill tone="warn" dot>Atención</Pill>
      : (ytLiveCount ?? 0) > 0
        ? <Pill tone="good" dot>Operativo</Pill>
        : <Pill tone="neutral">Sin señal</Pill>;

  return (
    <div>
      <PageHead title="Destinos de publicación" description="A dónde entregan tus canales. Administra credenciales, cuota y políticas en un solo lugar."
        actions={<><Btn variant="ghost" icon="fileText" onClick={() => navigate('/publishes')}>Publicaciones recientes</Btn><Btn variant="solid" icon="plus" onClick={() => setWiz(true)}>Editar destinos</Btn></>} />
      <Panel title={<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><Icon name="radio" size={22} color="var(--muted-slate)" /><span style={{ fontFamily: 'var(--font-display)', fontSize: 22 }}>YouTube Live</span>{headerPill}</div>}
        right={<AuditTrailLink />} style={{ marginBottom: 24 }}>
        {loading ? <SkeletonStatRow n={4} /> : (
        <div className="circa-grid-4">
          <StatCard label="Transmisiones activas" value={ytLiveCount ?? '—'} icon="radio" />
          <StatCard
            label="Cuota usada hoy"
            value={quota && quota.summary.projectCount > 0 ? `${quota.summary.quotaUsedPercent}%` : '—'}
            tone={quota && quota.summary.quotaUsedPercent >= 80 ? 'bad' : quota && quota.summary.quotaUsedPercent >= 50 ? 'warn' : undefined}
          />
          <StatCard
            label="Cuota restante"
            value={quota && quota.summary.projectCount > 0 ? `${quota.summary.quotaRemainingPercent}%` : '—'}
          />
          {/* Honest em-dash: no public failure-summary query exists for the
              publish ledger today; the slot will wire when one lands
              (NFR-006). */}
          <StatCard label="Publicaciones fallidas (24h)" value="—" hint="próximamente" />
        </div>
        )}
      </Panel>
      <Panel title="Capacidad de publicación" right={<HelpDot text="Circa rota las solicitudes de publicación entre varios proyectos para maximizar la cuota diaria." />}>
        {loading ? <SkeletonList rows={4} /> : !quota || quota.projects.length === 0 ? (
          <EmptyState
            icon="server"
            title="Sin proyectos de YouTube provisionados"
            body="Aún no hay proyectos de la API de YouTube configurados para este espacio. Cuando se provisione el primer proyecto aparecerá aquí su cuota y estado."
          />
        ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {quota.projects.map((p, i) => {
            const pct = p.quotaLimitDaily > 0 ? (p.quotaUsedToday / p.quotaLimitDaily) * 100 : 0;
            const tone = p.status === 'active' ? 'good' : p.status === 'rate-limited' ? 'warn' : 'neutral';
            const label = p.status === 'active' ? 'Activo' : p.status === 'rate-limited' ? 'Limitado' : 'Deshabilitado';
            return (
              <div key={p._id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 0', borderBottom: i < quota.projects.length - 1 ? '1px solid var(--lightest-gray)' : 'none' }}>
                <span style={{ width: 180, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.projectId}</span>
                <Pill tone={tone} size="sm">{label}</Pill>
                <div style={{ flex: 1 }}><Progress value={pct} color={tone === 'warn' ? 'var(--warn)' : 'var(--deep-green)'} /></div>
                <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', width: 150, textAlign: 'right' }}>{p.quotaUsedToday.toLocaleString('es-MX')} de {p.quotaLimitDaily.toLocaleString('es-MX')}</span>
              </div>
            );
          })}
        </div>
        )}
      </Panel>
      <DestinationsWizard open={wiz} onClose={() => setWiz(false)} />
    </div>
  );
}

